export interface FramingSetters {
  setZoom: (zoom: number) => void;
  setCrop: (crop: { x: number; y: number }) => void;
}

/**
 * Re-centers the logo framing editor: resets zoom now, and the crop offset on
 * the *next* frame.
 *
 * The two can't be set in one update: react-easy-crop reacts to prop changes
 * through an if/else-if chain (zoom, then crop), so when both change together
 * only the zoom branch runs and the crop branch — the one that re-emits the
 * newly centered crop area — never fires. Splitting them across frames makes
 * each branch run on its own. The same sequence is also needed once an image
 * finishes loading, because the initial `{x: 0, y: 0}`/`1` state values never
 * "change" from the library's point of view on mount.
 *
 * `isCancelled` is checked right before the deferred crop reset, so an
 * unmounted/superseded editor never gets a late update.
 */
export function recenterFraming(
  { setZoom, setCrop }: FramingSetters,
  options: {
    schedule?: (callback: () => void) => unknown;
    isCancelled?: () => boolean;
  } = {}
): void {
  const { schedule = requestAnimationFrame, isCancelled = () => false } =
    options;

  setZoom(1);
  schedule(() => {
    if (!isCancelled()) {
      setCrop({ x: 0, y: 0 });
    }
  });
}
